<template>
  <v-container class="rm-drawer-panel">
    <RightDrawerHeader
      :title="$t('REPAIR_FILE_DETAIL_ACTION_DRAWER.TITLE')"
      :showBorder="false"
      showClose
      @close="closeNewAction"
    />
    <v-row class="rm-drawer-scroll new-actions-content align-content-start">
      <v-col cols="12" class="d-flex flex-column ga-4">
        <p class="secondary-card-title mb-0">
          {{ $t('REPAIR_FILE_DETAIL_ACTION_DRAWER.SUB_TITLE.CUSTOMER_SUPPORT') }}
        </p>
        <v-btn-secondary
          class="w-100"
          variant="flat"
          :color="actionDisabled.stepper ? 'default' : 'primary'"
          :disabled="actionDisabled.stepper"
          :loading="loading"
          prepend-icon="mdi-file-outline"
          @click="routerPush"
        >
          {{ $t('REPAIR_FILE_DETAIL_ACTION_DRAWER.CUSTOMER_ESTIMATE_REVISION') }}
        </v-btn-secondary>
        <div class="d-flex align-center justify-center">
          <span
            class="status-dot bg-grey"
            :class="{
              [getDotClass('estimatePackage')]:
                currentRepairFile.status_detail ===
                RepairFileStatusDetailConstant.PRICING_SENT_TO_CUSTOMER
            }"
          />
          <p
            :class="{
              'text-grey':
                !checkRepairFileStatusDetailPricingSentToCustomerRelaunch() ||
                !currentRepairFile.estimate_package_last_relaunch_date
            }"
          >
            {{ $t('REPAIR_FILE_DETAIL_ACTION_DRAWER.LAST_RELAUNCH') }}
            {{
              getFormattedDate(
                currentRepairFile.estimate_package_last_relaunch_date,
                checkRepairFileStatusDetailPricingSentToCustomerRelaunch()
              )
            }}
          </p>
        </div>
      </v-col>

      <v-divider color="default" class="mx-4 my-2" />

      <v-col cols="12" class="d-flex flex-column ga-4">
        <p class="secondary-card-title mb-0">
          {{ $t('REPAIR_FILE_DETAIL_ACTION_DRAWER.REPAIRER_ESTIMATE') }}
        </p>
        <v-btn-secondary
          class="w-100"
          variant="flat"
          :color="actionDisabled.estimate ? 'default' : 'primary'"
          :disabled="actionDisabled.estimate"
          :loading="loading"
          prepend-icon="mdi-plus"
          @click="openEstimateRepairer()"
        >
          {{ $t('REPAIR_FILE_DETAIL_ACTION_DRAWER.ADD_ESTIMATE_TO_FILE') }}
        </v-btn-secondary>
        <div class="d-flex align-center justify-center">
          <span
            class="status-dot bg-grey"
            :class="{
              [getDotClass('repairerEstimate')]:
                currentRepairFile.status_detail ===
                RepairFileStatusDetailConstant.ESTIMATE_SENT_TO_CUSTOMER
            }"
          />
          <p
            :class="{
              'text-grey':
                !checkRepairFileStatusDetailEstimateSentToCustomerRelaunch() ||
                !currentRepairFile.repairer_estimate_last_relaunch_date
            }"
          >
            {{ $t('REPAIR_FILE_DETAIL_ACTION_DRAWER.LAST_RELAUNCH') }}
            {{
              getFormattedDate(
                currentRepairFile.repairer_estimate_last_relaunch_date,
                checkRepairFileStatusDetailEstimateSentToCustomerRelaunch()
              )
            }}
          </p>
        </div>
        <v-btn-secondary
          class="w-100"
          variant="flat"
          :color="actionDisabled.notifyRepairer ? 'default' : 'primary'"
          :disabled="actionDisabled.notifyRepairer"
          :loading="loading"
          prepend-icon="mdi-information-variant"
          @click="openEstimateRepairerDetail()"
        >
          {{ $t('REPAIR_FILE_DETAIL_ACTION_DRAWER.NOTIFY_THE_REPAIRER') }}
        </v-btn-secondary>
      </v-col>

      <v-divider color="default" class="mx-4 my-2" />

      <v-col cols="12" class="d-flex flex-column ga-4">
        <p class="secondary-card-title mb-0">
          {{ $t('REPAIR_FILE_DETAIL_ACTION_DRAWER.CLOSE_CASE') }}
        </p>
        <v-btn-secondary
          class="w-100 v-btn--no-hover"
          variant="flat"
          :color="actionDisabled.retrieveEquipment ? 'default' : 'primary'"
          :disabled="actionDisabled.retrieveEquipment"
          :loading="loading"
          prepend-icon="mdi-check"
          @click="openDeliveryClient('close')"
        >
          {{ $t('REPAIR_FILE_DETAIL_ACTION_DRAWER.RETRIEVE_EQUIPMENT_CUSTOMER') }}
        </v-btn-secondary>
      </v-col>
    </v-row>

    <v-row class="mt-auto flex-grow-0 flex-shrink-0">
      <v-col v-for="action in utilityActions" :key="action.key" cols="4">
        <v-btn-secondary
          class="button-square"
          :variant="action.key === 'cost' ? 'flat' : undefined"
          :color="action.key === 'cost' && !action.disabled ? 'primary' : 'default'"
          :disabled="action.disabled"
          :loading="loading"
          size="default"
          @click="action.onClick"
        >
          <div class="d-flex flex-column align-center ga-2">
            <v-icon :icon="action.icon" />
            <div class="square-button-label-wrapper">
              <p class="square-button-label line-clamp-3">{{ action.label }}</p>
              <v-tooltip activator="parent" location="bottom">{{ action.label }}</v-tooltip>
            </div>
          </div>
        </v-btn-secondary>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import RepairFileStatusDetailConstant from '@/constants/repair-module/repair-file/repairFileStatusDetailConstant'
import RepairFileService from '@/services/RepairFileService'

import { checkPermission } from '@/utils/permissionUtils'
import { displayToast, handleResponseError } from '@/utils/toastUtils'
import { displayModal } from '@/utils/modalUtils'
import RightDrawerHeader from '@/components/ui/RightDrawerHeader.vue'

export default {
  name: 'NewActions',
  components: { RightDrawerHeader },
  computed: {
    actionDisabled() {
      const cannotUpdate = !checkPermission(['DISTRIBUTOR_UPDATE_REPAIR_FILE'])
      return {
        stepper: this.checkRepairFileStatusDetailToGoStepper() || cannotUpdate,
        estimate: this.checkRepairFileStatusDetailToGoEstimateRepairer() || cannotUpdate,
        notifyRepairer:
          this.checkRepairFileStatusDetailToGoEstimateRepairerDetail() || cannotUpdate,
        retrieveEquipment:
          this.checkRepairFileStatusDetailToRetrieveEquipmentCustomer() ||
          !checkPermission(['DISTRIBUTOR_CLOSE_REPAIR_FILE'])
      }
    },
    RepairFileStatusDetailConstant() {
      return RepairFileStatusDetailConstant
    },
    utilityActions() {
      return [
        {
          key: 'rental',
          label: this.$t('REPAIR_FILE_DETAIL_ACTION_DRAWER.BUTTON.ADD_RENTAL'),
          icon: 'mdi-boomerang',
          disabled: !checkPermission(['DISTRIBUTOR_RENTAL_CREATE']),
          onClick: () => this.redirectToRentalPage()
        },
        {
          key: 'cost',
          label: this.$t('REPAIR_FILE_DETAIL_ACTION_DRAWER.BUTTON.CONFIRM_COST'),
          icon: 'mdi-flag-outline',
          disabled:
            this.checkRepairFileStatusDetailToConfirmCost() ||
            !checkPermission(['DISTRIBUTOR_UPDATE_REPAIR_FILE']),
          onClick: () => this.openDeliveryClient('update')
        },
        {
          key: 'delete',
          label: this.$t('COMMON.BUTTON.DELETE'),
          icon: 'mdi-trash-can-outline',
          disabled: !checkPermission(['DISTRIBUTOR_DELETE_REPAIR_FILE']),
          onClick: () => this.deleteRepairFile()
        }
      ]
    }
  },
  props: {
    currentRepairFile: Object,
    rentalFileId: Object
  },
  data() {
    return {
      loading: false,
      error: null
    }
  },
  methods: {
    checkPermission,
    openDeliveryClient(actionType) {
      const requiredPermission =
        actionType === 'close' ? 'DISTRIBUTOR_CLOSE_REPAIR_FILE' : 'DISTRIBUTOR_UPDATE_REPAIR_FILE'
      if (!checkPermission([requiredPermission])) {
        displayToast(this.$t('COMMON.NOT_AUTHORIZED_ACTION_MESSAGE'), 'warning')
        return
      }
      if (this.rentalFileId != null) {
        this.$emit('openRentalWarningModal')
      }

      this.$emit('openDeliveryClient', actionType)
    },
    openEstimateRepairer() {
      this.$emit('openEstimateRepairer')
    },
    openEstimateRepairerDetail() {
      this.$emit('openEstimateRepairerDetail')
    },
    checkRepairFileStatusDetailToRetrieveEquipmentCustomer() {
      const allowedStatuses = [
        RepairFileStatusDetailConstant.REPAIR_FILE_CREATED,
        RepairFileStatusDetailConstant.COLLECTION_TO_REPAIRER,
        RepairFileStatusDetailConstant.RECEPTION_AT_REPAIRER,
        RepairFileStatusDetailConstant.DIAGNOSTIC_MANUFACTURER_RECEIVED,
        RepairFileStatusDetailConstant.ESTIMATE_CREATED,
        RepairFileStatusDetailConstant.FORCE_ESTIMATE_CREATED,
        RepairFileStatusDetailConstant.ESTIMATE_SENT_TO_CUSTOMER,
        RepairFileStatusDetailConstant.FORCE_ESTIMATE_SENT_TO_CUSTOMER,
        RepairFileStatusDetailConstant.ESTIMATE_CUSTOMER_DECISION_SENT_TO_REPAIRER,
        RepairFileStatusDetailConstant.FORCE_ESTIMATE_CUSTOMER_DECISION_SENT_TO_REPAIRER,
        RepairFileStatusDetailConstant.IN_REPARATION,
        RepairFileStatusDetailConstant.SENT_BACK_BY_REPAIRER,
        RepairFileStatusDetailConstant.RECEIPT_AT_STORE,
        RepairFileStatusDetailConstant.SENT_AVAILABLE_TO_CUSTOMER
      ]

      return !allowedStatuses.includes(this.currentRepairFile.status_detail)
    },
    closeNewAction() {
      this.$emit('closeNewAction')
    },
    checkRepairFileStatusDetailPricingSentToCustomerRelaunch() {
      return (
        this.currentRepairFile.status_detail ===
        RepairFileStatusDetailConstant.PRICING_SENT_TO_CUSTOMER
      )
    },
    checkRepairFileStatusDetailEstimateSentToCustomerRelaunch() {
      return (
        this.currentRepairFile.status_detail ===
        RepairFileStatusDetailConstant.ESTIMATE_SENT_TO_CUSTOMER
      )
    },
    checkRepairFileStatusDetailToGoStepper() {
      const allowedStatuses = [
        RepairFileStatusDetailConstant.PRODUCT_SELECTED,
        RepairFileStatusDetailConstant.BREAKDOWN_EVALUATED,
        RepairFileStatusDetailConstant.GUARANTEE_EVALUATED,
        RepairFileStatusDetailConstant.PRICING_SENT_TO_CUSTOMER,
        RepairFileStatusDetailConstant.PRICING_EVALUATED,
        RepairFileStatusDetailConstant.INFORMATION_RETRIEVED,
        RepairFileStatusDetailConstant.PROSPECT_CLIENT_INFORMATION_RETRIEVED,
        RepairFileStatusDetailConstant.PROSPECT_REQUEST_SENT,
        RepairFileStatusDetailConstant.PROSPECT_MANUFACTURER_RESPONSE,
        RepairFileStatusDetailConstant.PROSPECT_MANUFACTURER_RESPONSE_VALIDATED,
        RepairFileStatusDetailConstant.TRANSPORTER_REPAIR_FILE_TRANSFER,
        RepairFileStatusDetailConstant.INTERNAL_COMMAND_NUMBER_LINKED,
        RepairFileStatusDetailConstant.FINALIZE_REPAIR_FILE
      ]

      return !allowedStatuses.includes(this.currentRepairFile.status_detail)
    },
    checkRepairFileStatusDetailToGoEstimateRepairer() {
      const allowedStatuses = [
        RepairFileStatusDetailConstant.REPAIR_FILE_CREATED,
        RepairFileStatusDetailConstant.COLLECTION_TO_REPAIRER,
        RepairFileStatusDetailConstant.RECEPTION_AT_REPAIRER,
        RepairFileStatusDetailConstant.DIAGNOSTIC_MANUFACTURER_RECEIVED
      ]

      return !allowedStatuses.includes(this.currentRepairFile.status_detail)
    },
    checkRepairFileStatusDetailToGoEstimateRepairerDetail() {
      const allowedStatuses = [
        RepairFileStatusDetailConstant.ESTIMATE_CREATED,
        RepairFileStatusDetailConstant.ESTIMATE_SENT_TO_CUSTOMER
      ]

      return !allowedStatuses.includes(this.currentRepairFile.status_detail)
    },
    checkRepairFileStatusDetailToConfirmCost() {
      const forbiddenStatuses = [
        RepairFileStatusDetailConstant.REPAIR_FILE_CREATED,
        RepairFileStatusDetailConstant.COLLECTION_TO_REPAIRER,
        RepairFileStatusDetailConstant.ESTIMATE_CREATED,
        RepairFileStatusDetailConstant.FORCE_ESTIMATE_CREATED,
        RepairFileStatusDetailConstant.ESTIMATE_SENT_TO_CUSTOMER,
        RepairFileStatusDetailConstant.FORCE_ESTIMATE_SENT_TO_CUSTOMER,
        RepairFileStatusDetailConstant.RECEIPT_AT_STORE,
        RepairFileStatusDetailConstant.SENT_AVAILABLE_TO_CUSTOMER,
        RepairFileStatusDetailConstant.TAKEOVER_BY_CUSTOMER,
        RepairFileStatusDetailConstant.NETWORK_BILLING,
        RepairFileStatusDetailConstant.FINISHED,
        RepairFileStatusDetailConstant.ABORT_BY_CUSTOMER,
        RepairFileStatusDetailConstant.DELETED
      ]

      return forbiddenStatuses.includes(this.currentRepairFile.status_detail)
    },
    routerPush() {
      switch (this.currentRepairFile.status_detail) {
        case RepairFileStatusDetailConstant.TRANSPORTER_REPAIR_FILE_TRANSFER:
          this.$router.push({
            name: 'repairFileShipping',
            params: { repairFileBoomerangId: this.currentRepairFile.boomerang_id }
          })
          break
        case RepairFileStatusDetailConstant.FINALIZE_REPAIR_FILE:
          this.$router.push({
            name: 'repairFileFinalization',
            params: { repairFileBoomerangId: this.currentRepairFile.boomerang_id }
          })
          break
        default:
          this.$router.push({
            name: 'repairFileCreate',
            query: { repairFileBoomerangId: this.$route.params.repairFileBoomerangId }
          })
      }
    },
    async deleteRepairFile() {
      const confirm = await displayModal({
        title: this.$t('REPAIR_FILE_DETAIL.MODAL.DELETE_REPAIR_FILE.TITLE'),
        message: this.$t('REPAIR_FILE_DETAIL.MODAL.DELETE_REPAIR_FILE.MESSAGE'),
        warning: this.$t('REPAIR_FILE_DETAIL.MODAL.DELETE_REPAIR_FILE.WARNING'),
        validateButton: this.$t('COMMON.BUTTON.DELETE'),
        cancelButton: this.$t('COMMON.BUTTON.CANCEL'),
        variant: 'destructive'
      })
      if (confirm) {
        try {
          this.loading = true
          const data = {
            repair_file_boomerang_id: this.currentRepairFile.boomerang_id
          }
          await RepairFileService.deleteRepairFile(data)
          displayToast(this.$t('REPAIR_FILE_DETAIL.MESSAGE.DELETE.SUCCESS'), 'success')
          this.$router.push({ name: 'dashboard' })
        } catch (error) {
          handleResponseError(error)
        } finally {
          this.loading = false
        }
      }
    },
    getFormattedDate(date, checkRepairFileStatusDetail) {
      if (checkRepairFileStatusDetail === false) {
        return 'XX/XX/XXXX'
      }
      if (date) {
        return new Date(date).toLocaleDateString()
      } else {
        return 'XX/XX/XXXX'
      }
    },
    getDotClass(typeRelaunch) {
      let last_relaunch_date = this.currentRepairFile.estimate_package_last_relaunch_date
      let workingDays = this.currentRepairFile.estimate_package_last_relaunch_date_day_count
      if (typeRelaunch === 'repairerEstimate') {
        last_relaunch_date = this.currentRepairFile.repairer_estimate_last_relaunch_date
        workingDays = this.currentRepairFile.repairer_estimate_last_relaunch_date_day_count
      }

      if (workingDays < 3 && last_relaunch_date) {
        return 'bg-success'
      } else if (workingDays >= 3 && workingDays < 6 && last_relaunch_date) {
        return 'bg-warning'
      } else if (workingDays >= 6 && last_relaunch_date) {
        return 'bg-error'
      }
      return 'bg-grey'
    },
    redirectToRentalPage() {
      this.$router.push({
        name: 'rentalProductList',
        params: { repairFileBoomerangId: this.currentRepairFile.boomerang_id }
      })
    }
  }
}
</script>

<style scoped>
.new-actions-content {
  flex: 0 1 auto;
}

.status-dot {
  margin-right: 10px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.button-square.v-btn {
  display: flex;
  width: 100%;
  height: 110px;
}

.square-button-label {
  width: 100%;
  text-align: center;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
}

.square-button-label-wrapper {
  width: 100%;
}
</style>
